import { makeStyles, Theme } from '@material-ui/core';
import Drawer from 'src/components/ui/drawer';
import { Heading5, Heading6 } from 'src/components/ui/typography';
import CloseIcon from '@material-ui/icons/Close';
import { grey } from 'src/data/colors';
import { DateRange } from '../../ui/controls/DateRange';
import { SecondaryButton, LinkButton, PrimaryButton } from '../../ui/Buttons';
import useTimeSelector from './useTimeSelector';
import { isBefore } from 'date-fns';

const useStyles = makeStyles((theme: Theme) => ({
  drawer: {
    flexDirection: 'column',
  },
  drawerContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    width: '100%',
    maxWidth: '480px',
  },
  contentContainer: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  drawerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px 16px 0 16px',
  },
  closeIcon: {
    color: grey,
  },
  footer: {
    padding: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    borderTop: `1px solid ${grey}`,
  },
  quickRanges: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  dateRangeContainer: {
    padding: '16px 16px 0 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flexShrink: 0,
  },
  dateRangeInputContainer: {
    marginTop: '4px',
  },
  quickRangeButtonsContainer: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
    minHeight: 0,
  },
  quickRangeButton: {
    padding: '4px 0px',
  },
  divider: {
    margin: '0px 16px',
    border: 'none',
    backgroundColor: grey,
    height: '1px',
  },
}));

interface TimeSelectorProps {
  isDrawerOpen: boolean;
  setIsDrawerOpen: (open: boolean) => void;
  setStartDate: (start: Date) => void;
  setEndDate: (end: Date) => void;
  start: Date | undefined;
  end: Date | undefined;
  loading: boolean;
  selectedQuickRange: string | undefined;
  setSelectedQuickRange: (quickRange: string) => void;
}

const TimeSelector = ({
  isDrawerOpen,
  setIsDrawerOpen,
  setStartDate,
  setEndDate,
  start,
  end,
  loading,
  selectedQuickRange,
  setSelectedQuickRange,
}: TimeSelectorProps) => {
  const classes = useStyles();

  const {
    setNewStart,
    setNewEnd,
    quickRange,
    updateDisabled,
    quickRangeHandler,
    applyTimeRange,
    QUICK_RANGES,
    newStart,
    newEnd,
  } = useTimeSelector({
    setStartDate,
    setEndDate,
    start,
    end,
    loading,
    selectedQuickRange,
    setSelectedQuickRange,
  });

  const handleUpdate = () => {
    applyTimeRange();
    setIsDrawerOpen(false);
  };

  const handleClose = () => {
    setIsDrawerOpen(false);
  };

  const handleClear = () => {
    setNewStart(undefined);
    setNewEnd(undefined);
    setSelectedQuickRange('');
  };

  return (
    <Drawer open={isDrawerOpen} onClose={handleClose} anchor="right">
      <div className={classes.drawerHeader}>
        <Heading5>Time Range</Heading5>
        <CloseIcon onClick={handleClose} className={classes.closeIcon} />
      </div>
      <div className={classes.drawerContent}>
        <div className={classes.contentContainer}>
          <div className={classes.dateRangeContainer}>
            <Heading6>Absolute time range</Heading6>
            <div className={classes.dateRangeInputContainer}>
              <DateRange
                start={newStart}
                end={newEnd}
                onStartChange={setNewStart}
                onEndChange={setNewEnd}
                disabled={loading}
                filterDates={(date) => {
                  return isBefore(date, new Date());
                }}
                maxRange={7}
              />
            </div>
          </div>
          <div className={classes.quickRanges}>
            <Heading6>Quick ranges</Heading6>
            <div className={classes.quickRangeButtonsContainer}>
              {QUICK_RANGES.map((range) => {
                const isSelected = quickRange === range.key;
                return (
                  <LinkButton
                    key={range.key}
                    onClick={() => quickRangeHandler(range.key)}
                    color={isSelected ? 'primaryGreen' : 'black'}
                    className={classes.quickRangeButton}
                  >
                    {range.label}
                  </LinkButton>
                );
              })}
            </div>
          </div>
        </div>
        <div className={classes.footer}>
          <SecondaryButton onClick={handleClear}>Clear</SecondaryButton>
          <PrimaryButton disabled={updateDisabled} onClick={handleUpdate}>
            Update
          </PrimaryButton>
        </div>
      </div>
    </Drawer>
  );
};

export default TimeSelector;
